// src/api/axiosClient.js
import axios from "axios";
import { msalInstance } from "../authConfig";
import { loginRequest } from "../authConfig";

const REAUTH_REDIRECT_STATE_KEY = "reauthRedirectState";
const REAUTH_COOLDOWN_MS = 2 * 60 * 1000;

const axiosClient = axios.create({
  baseURL: (process.env.REACT_APP_ADMIN_URL || "http://localhost:8000") + "/api",
  withCredentials: true,
});

function readReauthState() {
  try {
    const raw = sessionStorage.getItem(REAUTH_REDIRECT_STATE_KEY);
    return raw ? JSON.parse(raw) : { inFlight: false, lastAttemptAt: null };
  } catch (e) {
    return { inFlight: false, lastAttemptAt: null };
  }
}

function writeReauthState(state) {
  try {
    sessionStorage.setItem(REAUTH_REDIRECT_STATE_KEY, JSON.stringify(state));
  } catch (e) {
    // Ignore storage errors.
  }
}

function shouldAttemptReauthRedirect() {
  const state = readReauthState();
  if (!state.inFlight) return true;
  if (!state.lastAttemptAt) return false;
  return Date.now() - Number(state.lastAttemptAt) > REAUTH_COOLDOWN_MS;
}

async function tryGetToken() {
  const account = msalInstance.getActiveAccount();
  if (!account) return null;

  try {
    const result = await msalInstance.acquireTokenSilent({
      ...loginRequest,
      account,
    });
    return result.accessToken;
  } catch (e) {
    console.warn("Silent token acquisition failed:", e);
    return null;
  }
}

axiosClient.interceptors.request.use(async (config) => {
  const token = await tryGetToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor to handle re-authentication required
axiosClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Check if error indicates re-authentication is needed
    if (
      error.response?.status === 401 &&
      (error.response?.data?.detail?.includes("Re-authentication required") ||
       error.response?.data?.detail?.includes("re-authenticate") ||
       error.response?.data?.detail?.includes("missing claims"))
    ) {
      if (!shouldAttemptReauthRedirect()) {
        console.warn(
          "Re-authentication still failing after recent redirect; suppressing redirect loop."
        );
        return Promise.reject(error);
      }

      console.log("Re-authentication required - redirecting to B2C once.");

      writeReauthState({
        inFlight: true,
        lastAttemptAt: Date.now(),
      });
      
      // Clear any cached tokens
      const account = msalInstance.getActiveAccount();
      if (account) {
        try {
          // Clear the account from cache to force fresh login
          await msalInstance.removeAccount(account);
        } catch (e) {
          console.warn("Failed to remove account:", e);
        }
      }
      
      // Store the current URL to redirect back after re-auth
      sessionStorage.setItem(
        "redirectAfterLogin",
        `${window.location.pathname}${window.location.search}${window.location.hash}`
      );
      
      // Redirect to B2C login to get fresh token with claims
      try {
        await msalInstance.loginRedirect(loginRequest);
      } catch (e) {
        console.error("Failed to redirect to login:", e);
        writeReauthState({ inFlight: false, lastAttemptAt: Date.now() });
        // Fallback: redirect to login page
        window.location.href = "/login";
      }
      
      // Return a promise that never resolves to prevent further processing
      return new Promise(() => {});
    }
    
    // For other errors, pass them through
    return Promise.reject(error);
  }
);

export default axiosClient;
